/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { AnimateSharedLayout, motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { TagApi } from "../../../api/Tags";
import { ActiveCampaignAtom } from "../../../atoms/CampaignAtom";
import { SelectedTagsAtom } from "../../../atoms/SelectedTags";
import {
  CampaignsUsedInTagsAtom,
  IsCheckedAllTagsAtom,
  TagsAtom,
} from "../../../atoms/TagsAtom";
import {
  getComparator,
  Order,
  stableSort,
  TableFooter,
} from "../../../components/StyledTable";
import { ICategoryType } from "../../../generated/Api";
import useClickOutside from "../../../hook/useClickOutside";
import useRowsPerPage, { ITableName } from "../../../hook/useRowsPerPage";
import { notSelect } from "../../../styles/general";
import { ICampaignStatus } from "../../../types/CampaignTypes";
import { ITag } from "../../../types/TagTypes";
import StyledCheckBox from "../../StyledCheckBox";
import ContactsSelected from "../../Toasts/ContactsSelected";
import { TagsSearch } from "./Header";
import TableRow from "./TableRow";

const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: scroll;
  padding: 20px;

  height: calc(100vh - 145px);
`;

const TableCard = styled.div`
  min-width: 100%;
  width: max-content;

  border: 1px solid #eaeaeabf;
  background-color: #ffffff;

  display: grid;

  grid-template-columns:
    100px 140px 140px 120px repeat(4, 160px) 180px
    60px;

  grid-column-gap: 24px;

  padding: 15px 25px;

  align-items: center;
  justify-content: space-between;

  @media screen and (max-width: 1010px) {
    width: fit-content;
  }
`;

const TableHead = styled.div`
  color: #707070;
  display: flex;
  font-size: 14px;
  align-items: center;
  font-weight: 600;
  cursor: default;
  position: relative;
  ${notSelect}
`;

const TableActions = styled.div`
  color: #707070;
  display: flex;
  font-size: 14px;
  align-items: center;
  justify-content: flex-end;
  font-weight: 600;
  cursor: default;
  ${notSelect}
`;

const Icon = styled(motion.img)`
  width: 10px;
  height: 10px;
  margin-left: 8px;
  ${notSelect}
  cursor: pointer;
`;

const FlexRow = styled(motion.div)`
  display: flex;
  align-items: center;
  cursor: pointer;
  :hover {
    opacity: 0.6;
  }
`;

const SelectPopover = styled.div`
  position: absolute;
  background: white;
  z-index: 20;
  height: fit-content;
  width: 240px;
  overflow: auto;
  border-radius: 5px;
  top: 15;
  box-shadow: 0px 3px 20px #00000026;
  border: 1px solid #d4d4d4;
  border-bottom: none;
  border-top: none;
  display: flex;
  flex-direction: column;
`;

const TitlePopover = styled.div`
  font-size: 13px;
  color: #888888;
  margin-bottom: 5px;
  margin-left: 17px;
  padding-top: 18px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  padding: 10px;
  font-size: 14px;
  color: #2f2f2f;
  cursor: pointer;

  span {
    margin-left: 7px;
  }

  &:hover {
    background-color: #fb972e;
    color: #ffffff;
  }
`;

const divider = css`
  border-bottom: 1px solid #eaeaea;
`;

const WrapperCheckboxAndTitle = styled.div`
  display: flex;
  align-content: center;
  justify-content: start;
`;

export interface FilterOptions {
  status: ICampaignStatus[];
  labels: string[];
  noLabels: boolean;
}

export default function Table(): JSX.Element {
  let tagsPool = useRecoilValue(TagsAtom);
  const setActiveCampaign = useSetRecoilState(ActiveCampaignAtom);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof ITag>("name");
  const [selectedTags, setSelectedTags] = useRecoilState(SelectedTagsAtom);
  const tagsSearch = useRecoilValue(TagsSearch);
  const [openDropView, setOpenDropView] = useState(false);
  const selectCheckboxRef = useClickOutside(() => setOpenDropView(false));
  const [isCheckedTagsOnPage, setIsCheckedTagsOnPage] = useState(false);
  const [isCheckedAllTags, setIsCheckedAllTags] =
    useRecoilState(IsCheckedAllTagsAtom);
  const [cachedRowsPerPage, updateCachedRowsPerPage] = useRowsPerPage(
    ITableName.SETTINGS_TAGS
  );
  const [tagsRowsInPage, setTagsRowsInPage] = useState([]);
  const [campaignsUsedInTags, setCampaignsUsedInTags] = useRecoilState(
    CampaignsUsedInTagsAtom
  );
  const [totalTagsFromFilter, setTotalTagsFromFilter] = useState(
    tagsPool.length
  );

  useEffect(() => {
    setActiveCampaign(null);
  }, []);

  useEffect(() => {
    setPage(0);
    setSelectedTags([]);
    setIsCheckedAllTags(false);

    return () => {
      setPage(0);
      setSelectedTags([]);
      setIsCheckedAllTags(false);
    };
  }, []);

  useEffect(() => {
    (async () => {
      if (!tagsRowsInPage || !tagsRowsInPage.length) return;
      const tagIds = tagsRowsInPage.map((tag) => `${tag._id}`);
      const response = await TagApi.getCampaignsUsedInTags(tagIds);
      setCampaignsUsedInTags(response);
    })();
  }, [tagsRowsInPage]);

  useEffect(() => {
    if (tagsPool.length) {
      let tagsFiltered = tagsPool;

      if (tagsSearch.trim() !== "") {
        tagsFiltered = tagsPool.filter((tp) =>
          tp.name.toLowerCase().trim().includes(tagsSearch.trim().toLowerCase())
        );
      }

      let rowsInPage = stableSort(
        tagsFiltered.map((tag: ITag) => ({
          ...tag,
          name: tag.name.toLowerCase(),
          createdAt: tag.createdAt || new Date(),
          _id: tag._id || "",
          category: tag.category || "",
          type: tag.type || ICategoryType.User,
        })),
        getComparator(order, orderBy)
      )
        .slice(
          page * (cachedRowsPerPage || rowsPerPage),
          page * (cachedRowsPerPage || rowsPerPage) +
            (cachedRowsPerPage || rowsPerPage)
        )
        .map((row) => {
          let rowWithoutLowercase = tagsFiltered
            .map((tag: ITag) => ({
              ...tag,
              name: tag.name,
              _id: tag._id || "",
              createdAt: tag.createdAt || new Date(),
              usersTagged: 0,
            }))
            .find((r) => r._id === row._id) as typeof row;

          return rowWithoutLowercase;
        });

      setTagsRowsInPage(rowsInPage);
      setTotalTagsFromFilter(tagsFiltered.length);
    }
  }, [page, rowsPerPage, order, orderBy, tagsPool, tagsSearch]);

  const handleRequestSort = (property: keyof ITag) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  function handlePage(vle: number) {
    setPage(vle);
    setSelectedTags([]);
    setIsCheckedTagsOnPage(false);
  }

  function handleCheck(id: string) {
    setSelectedTags((elems) => {
      if (elems.includes(id)) {
        return elems.filter((check) => check !== id);
      }
      return [...elems, id];
    });
  }

  function unselectAll() {
    setSelectedTags([]);
    setIsCheckedTagsOnPage(false);
    setIsCheckedAllTags(false);
    setOpenDropView(false);
  }

  function selectAllTags() {
    const filteredTags = tagsSearch
      ? tagsPool.filter((tp) =>
          tp.name.toLowerCase().trim().includes(tagsSearch.trim().toLowerCase())
        )
      : tagsPool;

    setSelectedTags(
      filteredTags.map((tag) => {
        return tag._id;
      })
    );
    setOpenDropView(false);
    setIsCheckedTagsOnPage(false);
    setIsCheckedAllTags(true);
  }

  useEffect(() => {
    if (isCheckedAllTags) selectAllTags();
  }, [isCheckedAllTags, page, tagsPool]);

  return (
    <TableContainer>
      <div style={{ overflowX: "scroll" }}>
        <TableCard>
          <WrapperCheckboxAndTitle>
            <div style={{ position: "relative" }}>
              <StyledCheckBox
                customCss={css`
                  margin-right: 10px;
                  ${notSelect}
                `}
                check={isCheckedAllTags || isCheckedTagsOnPage}
                handleCheck={() => setOpenDropView(!openDropView)}
              />
              {openDropView && tagsPool && (
                <SelectPopover ref={selectCheckboxRef}>
                  <TitlePopover>SELECT</TitlePopover>

                  {!isCheckedTagsOnPage && (
                    <Row
                      onClick={() => {
                        setIsCheckedAllTags(false);
                        setIsCheckedTagsOnPage(true);
                        setOpenDropView(!openDropView);
                        setSelectedTags(tagsRowsInPage.map((t) => t._id));
                      }}
                      css={isCheckedAllTags && divider}
                    >
                      <span>
                        {tagsRowsInPage.length > 1
                          ? `Select ${tagsRowsInPage.length} tags on this page`
                          : `Select ${tagsRowsInPage.length} tag on this page`}
                      </span>
                    </Row>
                  )}

                  {!isCheckedAllTags && (
                    <Row
                      onClick={() => {
                        selectAllTags();
                      }}
                      css={divider}
                    >
                      <span>
                        {totalTagsFromFilter > 1
                          ? `Select all ${totalTagsFromFilter} tags`
                          : `Select all ${totalTagsFromFilter} tag`}
                      </span>
                    </Row>
                  )}

                  {selectedTags.length >= 1 && (
                    <Row onClick={unselectAll}>
                      <span>Deselect all</span>
                    </Row>
                  )}
                </SelectPopover>
              )}
            </div>
            <TableHead>
              <FlexRow onClick={() => handleRequestSort("type")}>
                TYPE
                <Icon
                  src={"./icons/contacts/arrow.svg"}
                  whileTap={{ scale: 0.8 }}
                />
              </FlexRow>
            </TableHead>
          </WrapperCheckboxAndTitle>

          <TableHead>
            <FlexRow onClick={() => handleRequestSort("category")}>
              CATEGORY
              <Icon
                src={"./icons/contacts/arrow.svg"}
                whileTap={{ scale: 0.8 }}
              />
            </FlexRow>
          </TableHead>

          <TableHead>
            <FlexRow onClick={() => handleRequestSort("name")}>
              TAG
              <Icon
                src={"./icons/contacts/arrow.svg"}
                whileTap={{ scale: 0.8 }}
              />
            </FlexRow>
          </TableHead>
          <TableHead>
            <FlexRow onClick={() => handleRequestSort("contactCount")}>
              USERS TAGGED
              <Icon
                src={"./icons/contacts/arrow.svg"}
                whileTap={{ scale: 0.8 }}
              />
            </FlexRow>
          </TableHead>

          <TableHead>
            <FlexRow
              css={css`
                cursor: auto;
                opacity: 1 !important;
              `}
            >
              CAMPAIGNS USED IN
            </FlexRow>
          </TableHead>

          <TableHead>
            <FlexRow
              css={css`
                cursor: auto;
                opacity: 1 !important;
              `}
            >
              CAMPAIGNS IT STARTS
            </FlexRow>
          </TableHead>

          <TableHead>
            <FlexRow
              css={css`
                cursor: auto;
                opacity: 1 !important;
              `}
            >
              CAMPAIGNS IT STOPS
            </FlexRow>
          </TableHead>

          <TableHead>
            <FlexRow
              css={css`
                cursor: auto;
                opacity: 1 !important;
              `}
            >
              FORMS USED IN
            </FlexRow>
          </TableHead>
          <TableHead>
            <FlexRow onClick={() => handleRequestSort("createdAt")}>
              DATE CREATED
              <Icon
                src={"./icons/contacts/arrow.svg"}
                whileTap={{ scale: 0.8 }}
              />
            </FlexRow>
          </TableHead>
          <TableActions>ACTIONS</TableActions>
        </TableCard>

        <div>
          <AnimateSharedLayout>
            {tagsRowsInPage.map((rowWithoutLowercase) => {
              const campaignsUsedInATag =
                campaignsUsedInTags.find(
                  (campaign) => campaign.tagId === rowWithoutLowercase._id
                )?.campaignsUsedIn || [];

              return (
                <TableRow
                  key={rowWithoutLowercase._id}
                  row={rowWithoutLowercase as any}
                  isChecked={selectedTags.includes(rowWithoutLowercase._id)}
                  handleCheck={() => handleCheck(rowWithoutLowercase._id)}
                  campaignsUsedIn={campaignsUsedInATag}
                />
              );
            })}
          </AnimateSharedLayout>
        </div>
      </div>
      <TableFooter
        page={page}
        length={tagsSearch ? totalTagsFromFilter : tagsPool.length || 0}
        rowsPerPage={cachedRowsPerPage || rowsPerPage}
        setRowsPerPage={(vle: number) => {
          setRowsPerPage(vle);
          updateCachedRowsPerPage(vle);
        }}
        handlePage={handlePage}
      />
    </TableContainer>
  );
}
